import { useBgColorCtx } from '../BgColorProvider'
import { TColorKey, TContrastLevel, TStatusColorKey } from '../typings'

type TOptions = {
  /**
   * how contrasted the icon should be on its background
   */
  contrast?: TContrastLevel
}

/**
 * based on provided contrast level and bgColor taken from closest BgColorProvider
 * returns a fgColor value that should be used for foreground (text/icon/...) with said contrast
 *
 * ! requires BgColorProvider somewhere above – where the closest bg color is set
 */
export const useFgColorValue = ({ contrast = `normal` }: TOptions) => {
  const { fgColors, isDisabledState, bgColor } = useBgColorCtx()

  // fg color on disabled elements should be less readable – intentionally
  // in BgColorProvider, the shade might be even flipped for disabled bgColors or states,
  // meaning there will be white text on light bgs instead of black
  if (isDisabledState) {
    return fgColors.low
  }

  // if the bgColor is one of the company colors, use high contrast
  if (shouldUseHighFgContrast(bgColor)) {
    return fgColors.high
  }

  // get the right color for the shade and requested contrast
  return fgColors[contrast]
}

/**
 * decides if the fg color should be high contrast (white) on the provided bgColor
 */
export const shouldUseHighFgContrast = (color: TColorKey) => {
  return isCompanyBgColor(color) || isStatusBgColor(color)
}

// * HELPERS

const COMPANY_COLOR_KEYS: RoA<TColorKey> = [
  `bgColors.company`,
  `activeColors.primary.hover`,
  `activeColors.primary.normal`,
  `activeColors.conversion.hover`,
  `activeColors.conversion.normal`,
  `specialColors.company`,
  `specialColors.speedlo`,
  `specialColors.mapElementHighlighted`,
]

// decides if the color is one of the company colors
const isCompanyBgColor = (color: TColorKey) => {
  return COMPANY_COLOR_KEYS.includes(color)
}

const STATUS_COLOR_KEYS: RoA<TStatusColorKey> = ['alert', 'error', 'info', 'success']

// decides if the color is one of the special colors
const isStatusBgColor = (color: TColorKey) => STATUS_COLOR_KEYS.some((key) => color.includes(key))
